import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { extractMarginsAndBody } from './paginationCore';
import { exportToDocx } from '../../utils/docxExporter';
import { formatLocalDateTime } from '../../utils/dateTime';
import TemplateEditor from './TemplateEditor';
import UnsavedChangesPopover from '../Common/UnsavedChangesPopover';
import DocumentEditor from './DocumentEditor';
import Toast from '../Common/Toast';
import { TEMPLATE_VARIABLES, VARIABLE_GROUPS } from '../../config/templateVariables';
import { renderConditionalBlocksToWrappers } from '../../utils/documentUtils';
import { extractTemplateMeta, pickHeaderFooterValue } from '../../utils/templateMeta';
import '../Common/CardActions.css';
import './Documents.css';
import OwnerBadge from '../Common/OwnerBadge';

const isAdminUser = (value) => (
  !!value?.is_staff || !!value?.is_superuser || String(value?.role || '').toLowerCase() === 'admin'
);

const DocumentsList = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [standardTemplates, setStandardTemplates] = useState([]);
  const [userTemplates, setUserTemplates] = useState([]);
  const [templateTypes, setTemplateTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingDocument, setEditingDocument] = useState(null);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [toast, setToast] = useState(null);
  const [sortField, setSortField] = useState('created_date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Состояние для диалога подтверждения
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Удалить',
    cancelText: 'Отмена'
  });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    try {
      const rawUser = localStorage.getItem('user');
      setCurrentUser(rawUser ? JSON.parse(rawUser) : null);
    } catch (_) {
      setCurrentUser(null);
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [documentsResponse, standardTemplatesResponse, userTemplatesResponse, templateTypesResponse] = await Promise.all([
        apiService.getDocuments(),
        apiService.getDocumentTemplates('standard'),
        apiService.getDocumentTemplates('user'),
        apiService.getTemplateTypes()
      ]);
      
      
      setDocuments(documentsResponse.data.results || []);
      setStandardTemplates(standardTemplatesResponse.data.results || []);
      setUserTemplates(userTemplatesResponse.data.results || []);
      setTemplateTypes(templateTypesResponse.data.results || []);
    } catch (error) {
      setError('Ошибка загрузки данных');
      console.error('Error loading data:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };


  const handleEditDocument = async (document) => {
    try {
      setError('');
      const response = await apiService.getDocument(document.id);
      setEditingDocument(response.data);
    } catch (error) {
      setError('Ошибка загрузки документа для редактирования');
      console.error('Error loading document:', error);
    }
  };

  const handleSaveDocument = async (documentId, templateData, { exit = false } = {}) => {
    try {
      const response = await apiService.patchDocument(documentId, {
        title: templateData.name,
        description: templateData.description,
        content: templateData.content
      });
      
      setDocuments(documents.map(doc => 
        doc.id === documentId ? response.data : doc
      ));
      showToast('Изменения сохранены');

      if (exit) {
        setEditingDocument(null);
      } else {
        setEditingDocument(prev => ({
          ...(prev || {}),
          ...response.data
        }));
      }
    } catch (error) {
      setError('Ошибка сохранения документа');
      console.error('Error saving document:', error);
      throw error;
    }
  };

  const handleDeleteDocument = (documentId) => {
    const document = documents.find(doc => doc.id === documentId);
    const documentTitle = document ? document.title : `документ ${documentId}`;
    
    showConfirmDialog(
      'Подтверждение удаления',
      `Вы уверены, что хотите удалить ${documentTitle}? Это действие нельзя отменить.`,
      async () => {
        try {
          await apiService.deleteDocument(documentId);
          setDocuments(documents.filter(doc => doc.id !== documentId));
        } catch (error) {
          setError('Ошибка удаления документа');
          console.error('Error deleting document:', error);
        }
      }
    );
  };

  const handleDownloadWord = async (documentId, documentTitle) => {
    try {
      const response = await apiService.getDocument(documentId);
      const parsed = extractTemplateMeta(response.data?.content || '');
      const content = parsed.content || '';
      const headerContent = response.data?.header_content || '';
      const footerContent = response.data?.footer_content || '';
      const { margins, bodyHTML } = extractMarginsAndBody(content);
      const htmlContent = bodyHTML || content;
      
      // Формируем имя файла
      const date = new Date();
      const dateStr = date.toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const fileName = `${documentTitle}_${dateStr}.docx`;
      await exportToDocx(htmlContent, fileName.replace(/\.docx$/i, ''), margins || {}, {
        headerContent: headerContent || parsed.header_content || '',
        footerContent: footerContent || parsed.footer_content || '',
        headerGapPx: parsed.header_gap_px,
        footerGapPx: parsed.footer_gap_px,
      });
    } catch (error) {
      setError('Ошибка при скачивании документа');
      console.error('Error downloading document:', error);
    }
  };

  const handleViewTemplate = async (template) => {
    try {
      setError('');
      const response = await apiService.getDocumentTemplate(template.id);
      setViewingTemplate(response.data);
    } catch (error) {
      setError('Ошибка загрузки шаблона');
      console.error('Error loading template:', error);
    }
  };

  const handleDuplicateTemplate = async (templateId) => {
    try {
      setError('');
      await apiService.duplicateDocumentTemplate(templateId);
      // Перезагружаем пользовательские шаблоны
      const userTemplatesResponse = await apiService.getDocumentTemplates('user');
      setUserTemplates(userTemplatesResponse.data.results || []);
    } catch (error) {
      setError('Ошибка копирования шаблона');
      console.error('Error duplicating template:', error);
    }
  };

  const handleDeleteTemplate = (templateId) => {
    // Ищем шаблон как в пользовательских, так и в стандартных
    const template = userTemplates.find(t => t.id === templateId) || 
                    standardTemplates.find(t => t.id === templateId);
    const templateName = template ? template.name : `шаблон ${templateId}`;
    
    showConfirmDialog(
      'Подтверждение удаления',
      `Вы уверены, что хотите удалить шаблон ${templateName}? Это действие нельзя отменить.`,
      async () => {
        try {
          await apiService.deleteDocumentTemplate(templateId);
          // Обновляем списки без перезагрузки страницы (используем функциональный set, чтобы избежать "устаревшего" state)
          setStandardTemplates((prev) => prev.filter(t => t.id !== templateId));
          setUserTemplates((prev) => prev.filter(t => t.id !== templateId));
        } catch (error) {
          setError('Ошибка удаления шаблона');
          console.error('Error deleting template:', error);
        }
      }
    );
  };

  const handleEditTemplate = async (template) => {
    try {
      setError('');
      const response = await apiService.getDocumentTemplate(template.id);
      const parsed = extractTemplateMeta(response.data?.content || '');
      setEditingTemplate({
        ...response.data,
        header_content: pickHeaderFooterValue(response.data?.header_content, parsed.header_content, ''),
        footer_content: pickHeaderFooterValue(response.data?.footer_content, parsed.footer_content, ''),
      });
    } catch (error) {
      setError('Ошибка загрузки шаблона для редактирования');
      console.error('Error loading template for editing:', error);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate({
      name: '',
      description: '',
      content: '',
      header_content: '',
      footer_content: ''
    });
  };

  const handleSaveTemplate = async (templateId, templateData, { exit = false } = {}) => {
    try {
      console.log('handleSaveTemplate called with:', { templateId, templateData });
      
      // Проверяем токены
      const tokens = localStorage.getItem('tokens');
      console.log('Tokens available:', tokens ? 'yes' : 'no');
      
      let response;
      let savedTemplate;

      if (templateId) {
        // Обновление существующего шаблона
        console.log('Updating existing template with ID:', templateId);
        response = await apiService.updateDocumentTemplate(templateId, templateData);
        const parsed = extractTemplateMeta(response.data?.content || '');
        savedTemplate = {
          ...templateData,
          ...(response.data || {}),
          header_content: Object.prototype.hasOwnProperty.call(response.data || {}, 'header_content')
            ? pickHeaderFooterValue(response.data.header_content, parsed.header_content, templateData.header_content || '')
            : templateData.header_content,
          footer_content: Object.prototype.hasOwnProperty.call(response.data || {}, 'footer_content')
            ? pickHeaderFooterValue(response.data.footer_content, parsed.footer_content, templateData.footer_content || '')
            : templateData.footer_content,
        };
        
        // Проверяем, является ли шаблон стандартным
        const isStandard = editingTemplate && editingTemplate.is_standard;
        
        if (isStandard) {
          // Обновляем список стандартных шаблонов
          setStandardTemplates(standardTemplates.map(t => 
            t.id === templateId ? { ...t, ...response.data } : t
          ));
        } else {
          // Обновляем список пользовательских шаблонов
          setUserTemplates(userTemplates.map(t => 
            t.id === templateId ? { ...t, ...response.data } : t
          ));
        }
      } else {
        // Создание нового шаблона (всегда пользовательский)
        console.log('Creating new template');
        response = await apiService.createDocumentTemplate(templateData);
        const parsed = extractTemplateMeta(response.data?.content || '');
        savedTemplate = {
          ...templateData,
          ...(response.data || {}),
          header_content: Object.prototype.hasOwnProperty.call(response.data || {}, 'header_content')
            ? pickHeaderFooterValue(response.data.header_content, parsed.header_content, templateData.header_content || '')
            : templateData.header_content,
          footer_content: Object.prototype.hasOwnProperty.call(response.data || {}, 'footer_content')
            ? pickHeaderFooterValue(response.data.footer_content, parsed.footer_content, templateData.footer_content || '')
            : templateData.footer_content,
        };
        setUserTemplates([savedTemplate, ...userTemplates]);
      }
      showToast('Изменения сохранены');

      if (exit) {
        setEditingTemplate(null);
      } else if (savedTemplate) {
        setEditingTemplate(prev => ({
          ...(prev || {}),
          ...savedTemplate
        }));
      }
      console.log('Template saved successfully');
      return savedTemplate || null;
    } catch (error) {
      setError('Ошибка сохранения шаблона');
      console.error('Error saving template:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      throw error; // Пробрасываем ошибку, чтобы редактор мог её обработать
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const sortedDocuments = [...documents].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case 'title':
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
        break;
      case 'template_name':
        aValue = a.template_name.toLowerCase();
        bValue = b.template_name.toLowerCase();
        break;
      case 'created_date':
        aValue = new Date(a.created_date);
        bValue = new Date(b.created_date);
        break;
      case 'updated_date':
        aValue = new Date(a.updated_date || a.created_date);
        bValue = new Date(b.updated_date || b.created_date);
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  
  const formatDate = (dateString) => {
    return formatLocalDateTime(dateString);
  };


  // Функции для управления диалогом подтверждения
  const showConfirmDialog = (title, message, onConfirm, confirmText = 'Удалить') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      confirmText,
      cancelText: 'Отмена'
    });
  };

  const hideConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  };

  const handleConfirmAction = () => {
    if (confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    hideConfirmDialog();
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Загрузка документов...</div>
      </div>
    );
  }

  return (
    <div className="documents-container">
      <div className="documents-layout">
        {/* Левая колонка - Мои документы */}
        <div className="documents-left-column">
          <div className="page-header">
            <h2>Мои документы</h2>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="documents-list">
            {documents.length === 0 ? (
              <div className="empty-state">
                <p>У вас пока нет документов</p>
                <p>Документы создаются из карточек торгов</p>
              </div>
            ) : (
              <table className="documents-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}></th>
                    <th 
                      className={`sortable ${sortField === 'title' ? `sort-${sortDirection}` : ''}`}
                      onClick={() => handleSort('title')}
                    >
                      Название
                    </th>
                    <th 
                      className={`sortable ${sortField === 'created_date' ? `sort-${sortDirection}` : ''}`}
                      onClick={() => handleSort('created_date')}
                    >
                      Создан
                    </th>
                    <th 
                      className={`sortable ${sortField === 'updated_date' ? `sort-${sortDirection}` : ''}`}
                      onClick={() => handleSort('updated_date')}
                    >
                      Изменен
                    </th>
                    <th className="document-table-actions">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDocuments.map((document) => (
                    <tr 
                      key={document.id}
                      onClick={() => handleEditDocument(document)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="word-icon-compact">
                          <div className="word-icon-header">
                            <div className="word-ribbon"></div>
                            <div className="word-ribbon"></div>
                            <div className="word-ribbon"></div>
                          </div>
                          <div className="word-icon-body">
                            <div className="word-letter">W</div>
                          </div>
                          <div className="word-icon-corner"></div>
                        </div>
                      </td>
                      <td>
                        <div className="document-table-title">
                          {document.title}
                          <div style={{ marginTop: 6 }}>
                            <OwnerBadge email={document.owner_email} />
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="document-table-date">{formatDate(document.created_date)}</div>
                      </td>
                      <td>
                        <div className="document-table-date">
                          {document.updated_date ? formatDate(document.updated_date) : '-'}
                        </div>
                      </td>
                      <td className="document-table-actions">
                        <div className="card-actions documents-table-actions">
                          <button
                            type="button"
                            className="card-action-btn card-action-btn--download"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadWord(document.id, document.title);
                            }}
                            title="Скачать в формате Word"
                          >
                            ⬇
                          </button>
                          <button
                            type="button"
                            className="card-action-btn card-action-btn--delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteDocument(document.id);
                            }}
                            title="Удалить документ"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Правая колонка - Шаблоны */}
        <div className="documents-right-column">
          {/* Стандартные шаблоны */}
          <div className="templates-section">
            <div className="section-header">
              <h3>Стандартные шаблоны документов</h3>
            </div>
            <div className="templates-list">
              {standardTemplates.map(template => (
                <div key={template.id} className="template-item">
                  <div className="template-info">
                    <h4 style={{ marginBottom: 6 }}>{template.name}</h4>
                    <OwnerBadge email={template.owner_email} />
                    {template.template_type_name && (
                      <div className="template-type-badge">
                        {template.template_type_name}
                      </div>
                    )}
                    {template.description && (
                      <p className="template-description">{template.description}</p>
                    )}
                  </div>
                  <div className="template-actions">
                    <button
                      className="btn btn-link btn-sm"
                      onClick={() => handleViewTemplate(template)}
                    >
                      Просмотр
                    </button>
                    <button
                      className="btn btn-link btn-sm"
                      onClick={() => handleDuplicateTemplate(template.id)}
                    >
                      Копировать
                    </button>
                    {template.can_edit && (
                      <button
                        className="btn btn-link btn-sm"
                        onClick={() => handleEditTemplate(template)}
                      >
                        Изменить
                      </button>
                    )}
                    {template.can_delete && (
                      <button
                        className="btn btn-link btn-sm text-danger"
                        onClick={() => handleDeleteTemplate(template.id)}
                      >
                        Удалить
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="templates-divider"></div>

          {/* Пользовательские шаблоны */}
          <div className="templates-section">
            <div className="section-header">
              <h3>Пользовательские шаблоны документов</h3>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={handleCreateTemplate}
                title="Создать новый шаблон"
              >
                +
              </button>
            </div>
            <div className="templates-list">
              {userTemplates.length === 0 ? (
                <div className="empty-state">
                  <p>{isAdminUser(currentUser) ? 'Пользовательских шаблонов пока нет' : 'У вас пока нет собственных шаблонов'}</p>
                </div>
              ) : (
                userTemplates.map(template => (
                  <div key={template.id} className="template-item">
                    <div className="template-info">
                      <h4 style={{ marginBottom: 6 }}>{template.name}</h4>
                      <OwnerBadge email={template.owner_email} />
                      {template.template_type_name && (
                        <div className="template-type-badge">
                          {template.template_type_name}
                        </div>
                      )}
                      {template.description && (
                        <p className="template-description">{template.description}</p>
                      )}
                    </div>
                    <div className="template-actions">
                      <button
                        className="btn btn-link btn-sm"
                        onClick={() => handleViewTemplate(template)}
                      >
                        Просмотр
                      </button>
                      <button
                        className="btn btn-link btn-sm"
                        onClick={() => handleDuplicateTemplate(template.id)}
                      >
                        Копировать
                      </button>
                      {template.can_edit && (
                        <button
                          className="btn btn-link btn-sm"
                          onClick={() => handleEditTemplate(template)}
                        >
                          Изменить
                        </button>
                      )}
                      {template.can_delete && (
                        <button
                          className="btn btn-link btn-sm text-danger"
                          onClick={() => handleDeleteTemplate(template.id)}
                        >
                          Удалить
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {editingDocument && (
        <DocumentEditor
          document={{
            id: editingDocument.id,
            name: editingDocument.title,
            content: editingDocument.content,
            header_content: editingDocument.header_content || '',
            footer_content: editingDocument.footer_content || '',
          }}
          templateName={editingDocument.template_name}
          onSave={handleSaveDocument}
          onCancel={() => setEditingDocument(null)}
        />
      )}

      {viewingTemplate && (
        <TemplateViewer
          template={viewingTemplate}
          onClose={() => setViewingTemplate(null)}
        />
      )}

      {editingTemplate && (
        <TemplateEditor
          template={editingTemplate}
          onSave={handleSaveTemplate}
          onCancel={() => setEditingTemplate(null)}
          templateTypes={templateTypes}
        />
      )}

      {/* Диалог подтверждения */}
      {confirmDialog.isOpen && (
        <ConfirmDialog 
          confirmDialog={confirmDialog}
          onCancel={hideConfirmDialog}
          onConfirm={handleConfirmAction}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
};


const TemplateViewer = ({ template, onClose }) => {
  const templateParsed = extractTemplateMeta(template?.content || '');
  const viewerHeader = String(template?.header_content || templateParsed.header_content || '');
  const viewerFooter = String(template?.footer_content || templateParsed.footer_content || '');
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="template-viewer-modal">
        <div className="viewer-header">
          <h3>{template.name}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        
        <div className="viewer-body">
          {!!viewerHeader && (
            <div style={{ marginBottom: 12 }}>
              <h4 style={{ marginBottom: 6 }}>Header</h4>
              <div
                className="template-content"
                dangerouslySetInnerHTML={{ __html: renderConditionalBlocksToWrappers(viewerHeader) }}
              />
            </div>
          )}
          <div
            className="template-content"
            dangerouslySetInnerHTML={{ __html: renderConditionalBlocksToWrappers(templateParsed.content || '') }}
          />
          {!!viewerFooter && (
            <div style={{ marginTop: 12 }}>
              <h4 style={{ marginBottom: 6 }}>Footer</h4>
              <div
                className="template-content"
                dangerouslySetInnerHTML={{ __html: renderConditionalBlocksToWrappers(viewerFooter) }}
              />
            </div>
          )}
          
          <div className="variables-legend">
            <h4>Переменные шаблона:</h4>
            <div className="variables-groups-viewer">
              {Object.entries(VARIABLE_GROUPS).map(([groupName, variables]) => (
                <div key={groupName} className="variable-group-viewer">
                  <div className="group-title-viewer">{groupName}</div>
                  <div className="variables-chips-viewer">
                    {variables.map(variable => (
                      <div
                        key={variable}
                        className="variable-chip-viewer"
                        title={TEMPLATE_VARIABLES[variable]}
                      >
                        {`{{${variable}}}`}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="variables-legend-hint">
              💡 Наведите курсор на переменную для просмотра описания
            </div>
          </div>
        </div>
        
        <div className="viewer-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

// Компонент диалога подтверждения с обработчиком Escape
const ConfirmDialog = ({ confirmDialog, onCancel, onConfirm }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation(); 
        e.stopImmediatePropagation();
        onCancel();
        return false;
      }
    };

    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [onCancel]);

  return (
    <UnsavedChangesPopover
      isOpen={!!confirmDialog?.isOpen}
      title={confirmDialog?.title || 'Подтверждение'}
      message={confirmDialog?.message}
      onCancel={onCancel}
      onConfirm={onConfirm}
      cancelText={confirmDialog?.cancelText || 'Отмена'}
      confirmText={confirmDialog?.confirmText || 'Подтвердить'}
      zIndex={10000}
    />
  );
};

export default DocumentsList;
